import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import {
  Button, Typography, message, Card, Table, Tag, Space,
  Divider, Input, Select, Row, Col, Progress, Spin, Tooltip,
} from "antd";
import {
  SearchOutlined, ClearOutlined, FileExcelOutlined,
  FilterOutlined, LeftOutlined, RightOutlined, DownloadOutlined, BarChartOutlined,
} from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import logo from "../../assets/Logo_Prosperidad_Social.png";
import {
  obtenerInformeTitularesPaginado,
  obtenerInformeTitularesPorDocumento,
  obtenerInformeTitularesPorMunicipio,
  obtenerInformeTitularesPorDepartamento,
  obtenerInformeTitularesPorEncuesta,
  obtenerInformeTitularesPorParticipacion,
  obtenerInformeTitularesPorBeneficiario,
  obtenerMunicipiosInforme,
  obtenerDepartamentosInforme,
  obtenerTotalInformeTitulares,
  obtenerTodosInformeTitularesEnBloques,
  obtenerInformeTitularesPorEncuestador,
  obtenerInformeTitularesPorAprobacionPago,
  obtenerEncuestadoresInforme,
} from "../../services/informeTitulares/informeGeneralTitularesService";

const { Title, Text } = Typography;

const PAGE_SIZE_OPTIONS = [300, 500, 1000];
const DEFAULT_PAGE_SIZE = 300;
const DEBOUNCE_MS       = 500;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const etiquetaEncuesta = (val) =>
  val === "REALIZADA"
    ? <Tag color="success">Realizada</Tag>
    : <Tag color="default">No realizada</Tag>;

const etiquetaParticipacion = (val) => {
  if (val === "PARTICIPA")    return <Tag color="success">Participa</Tag>;
  if (val === "NO PARTICIPA") return <Tag color="error">No participa</Tag>;
  return <Tag color="warning">Sin definir</Tag>;
};

const etiquetaBeneficiario = (val) =>
  val === "SI"
    ? <Tag color="blue">Sí</Tag>
    : <Tag color="default">No</Tag>;

// ─── Componente principal ──────────────────────────────────────────────────────

const InformeGeneralTitulares = () => {
  const appName     = "informeGeneralTitulares";
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const esAdmin     = useSelector((state) => state.auth.user?.role === "1");
  const userState   = useSelector((state) => state.auth.user);

  // ── Datos ──────────────────────────────────────────────────────────────────
  const [rawData,        setRawData]        = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loadingTabla,   setLoadingTabla]   = useState(false);

  // ── Paginación por cursor ──────────────────────────────────────────────────
  const [cursors,     setCursors]     = useState([""]);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [nextCursor,  setNextCursor]  = useState(null);
  const [pageSize,    setPageSize]    = useState(DEFAULT_PAGE_SIZE);
  const [modoFiltro,  setModoFiltro]  = useState("paginado");

  // ── Catálogos ──────────────────────────────────────────────────────────────
  const [municipios,           setMunicipios]           = useState([]);
  const [departamentos,        setDepartamentos]        = useState([]);
  const [loadingMunicipios,    setLoadingMunicipios]    = useState(false);
  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [encuestadores, setEncuestadores] = useState([]);
  const [loadingEncuestadores, setLoadingEncuestadores] = useState(false);

  // ── Filtros de selector (admin) ────────────────────────────────────────────
  const [municipioAdmin,    setMunicipioAdmin]    = useState(null);
  const [departamentoAdmin, setDepartamentoAdmin] = useState(null);

  // ── Filtros de tabla ───────────────────────────────────────────────────────
  const [filtroDocumento,     setFiltroDocumento]     = useState("");
  const [filtroEncuesta,      setFiltroEncuesta]      = useState(null);
  const [filtroParticipacion, setFiltroParticipacion] = useState(null);
  const [filtroBeneficiario,  setFiltroBeneficiario]  = useState(null);
  const [filtroEncuestador, setFiltroEncuestador] = useState(null);
  const [filtroAprobacionPago, setFiltroAprobacionPago] = useState(null);

  // ── Descarga Excel ─────────────────────────────────────────────────────────
  const [descargando,      setDescargando]      = useState(false);
  const [progresoDescarga, setProgresoDescarga] = useState(0);
  const [filasDescargadas, setFilasDescargadas] = useState(0);

  // ── Resumen por municipio ──────────────────────────────────────────────────
  const [mostrarResumenMunicipio, setMostrarResumenMunicipio] = useState(false);
  const [rawDataResumen,          setRawDataResumen]          = useState([]);
  const [loadingResumen,          setLoadingResumen]          = useState(false);
  const [progresoResumen,         setProgresoResumen]         = useState(0);
  const [filasResumen,            setFilasResumen]            = useState(0);

  const debounceDocRef = useRef(null);

  // ── Carga inicial ──────────────────────────────────────────────────────────
  useEffect(() => {
    cargarMunicipios();
    cargarDepartamentos();
    cargarEncuestadores();
    cargarPaginado("", DEFAULT_PAGE_SIZE, true);
    obtenerTotalInformeTitulares()
      .then(setTotalRegistros)
      .catch(console.error);
  }, []);

  // ── Mapeo de filas ─────────────────────────────────────────────────────────
  const mapearFilas = (items) =>
    items.map((item, idx) => ({
      key:                 item.num_identificacion ?? idx,
      numIdentificacion:   item.num_identificacion   || "—",
      nombreCompleto:      item.nombre_completo       || "—",
      celular:             item.celular               || "—",
      correo:              item.correo                || "—",
      departamento:        item.departamento          || "—",
      municipio:           item.municipio             || "—",
      encuesta:            item.encuesta              || "NO REALIZADA",
      estadoParticipacion: item.estado_participacion  || "SIN DEFINIR",
      beneficiario:        item.beneficiario          || "NO",
      encuestador:         item.encuestador_nombres && item.encuestador_apellidos
      ? `${item.encuestador_nombres} ${item.encuestador_apellidos}`: "—",
      aprobacionPago:     item.aprobacion_pago  || "NO",
      entrega2:           item.entrega_2         || "NO",
      entrega3:           item.entrega_3         || "NO",
      _raw: item,
    }));

  const mapearFilasExport = (items) =>
    items.map((item) => ({
      "N° Identificación":    item.num_identificacion   || "—",
      "Nombre Completo":      item.nombre_completo       || "—",
      "Celular":              item.celular               || "—",
      "Correo":               item.correo                || "—",
      "Departamento":         item.departamento          || "—",
      "Municipio":            item.municipio             || "—",
      "Encuesta":             item.encuesta              || "NO REALIZADA",
      "Estado Participación": item.estado_participacion  || "SIN DEFINIR",
      "Beneficiario":         item.beneficiario          || "NO",
      "Encuestador":          item.encuestador_nombres && item.encuestador_apellidos
          ? `${item.encuestador_nombres} ${item.encuestador_apellidos}`
          : "—",
      "Aprobación Pago":      item.aprobacion_pago  || "NO",
      "Entregas 1":           item.beneficiario     || "NO",
      "Entregas 2":           item.entrega_2        || "NO",
      "Entregas 3":           item.entrega_3        || "NO",
    }));

  // ── Paginación ─────────────────────────────────────────────────────────────
  const cargarPaginado = useCallback(async (
    lastId     = "",
    size       = DEFAULT_PAGE_SIZE,
    reiniciar  = false,
    silencioso = false,
  ) => {
    if (!silencioso) setLoadingTabla(true);
    try {
      const resultado = await obtenerInformeTitularesPaginado(lastId, size);
      setRawData(mapearFilas(resultado.data));
      setHasMore(resultado.hasMore);
      setNextCursor(resultado.nextCursor);
      setModoFiltro("paginado");
      limpiarFiltrosSinRecargar();
      if (!silencioso)
        resultado.data.length > 0
          ? message.success(`${resultado.data.length} registros cargados`)
          : message.warning("No se encontraron registros");
      if (reiniciar) { setCursors([""]); setCursorIndex(0); }
    } catch (error) {
      console.error(error);
      if (!silencioso) message.error("Error al consultar el informe general");
    } finally {
      if (!silencioso) setLoadingTabla(false);
    }
  }, []);

  const irSiguientePagina = () => {
    if (!hasMore || nextCursor === null) return;
    const nuevosCursors = [...cursors.slice(0, cursorIndex + 1), nextCursor];
    setCursors(nuevosCursors);
    const nuevoIndex = cursorIndex + 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(nextCursor, pageSize);
  };

  const irPaginaAnterior = () => {
    if (cursorIndex === 0) return;
    const nuevoIndex = cursorIndex - 1;
    setCursorIndex(nuevoIndex);
    cargarPaginado(cursors[nuevoIndex], pageSize);
  };

  const handlePageSizeChange = (nuevoSize) => {
    setPageSize(nuevoSize);
    cargarPaginado("", nuevoSize, true);
  };

  // ── Catálogos ──────────────────────────────────────────────────────────────
  const cargarMunicipios = useCallback(async () => {
    setLoadingMunicipios(true);
    try {
      const resultado = await obtenerMunicipiosInforme();
      if (resultado?.success) setMunicipios(resultado.data);
    } catch { message.error("Error al cargar municipios"); }
    finally { setLoadingMunicipios(false); }
  }, []);

  const cargarDepartamentos = useCallback(async () => {
    setLoadingDepartamentos(true);
    try {
      const resultado = await obtenerDepartamentosInforme();
      if (resultado?.success) setDepartamentos(resultado.data);
    } catch { message.error("Error al cargar departamentos"); }
    finally { setLoadingDepartamentos(false); }
  }, []);

  const cargarEncuestadores = useCallback(async () => {
    setLoadingEncuestadores(true);

    try {
      const resultado = await obtenerEncuestadoresInforme();

      if (resultado?.success) {
        setEncuestadores(resultado.data);
      }
    } catch {
      message.error("Error al cargar encuestadores");
    } finally {
      setLoadingEncuestadores(false);
    }
  }, []);

  // ── Handlers selectores admin ──────────────────────────────────────────────
  const handleMunicipioAdminChange = useCallback(async (value) => {
    setMunicipioAdmin(value ?? null);
    setDepartamentoAdmin(null);
    if (!value) { await cargarPaginado("", pageSize, true); return; }
    setLoadingTabla(true);
    try {
      const resultado = await obtenerInformeTitularesPorMunicipio(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("municipio");
      setHasMore(false);
      limpiarFiltrosSinRecargar();
      message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del municipio`);
    } catch (error) {
      console.error(error);
      message.error("Error al filtrar por municipio");
    } finally { setLoadingTabla(false); }
  }, [pageSize, cargarPaginado]);

  const handleDepartamentoAdminChange = useCallback(async (value) => {
    setDepartamentoAdmin(value ?? null);
    setMunicipioAdmin(null);
    if (!value) { await cargarPaginado("", pageSize, true); return; }
    setLoadingTabla(true);
    try {
      const resultado = await obtenerInformeTitularesPorDepartamento(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("departamento");
      setHasMore(false);
      limpiarFiltrosSinRecargar();
      message.success(`${resultado.data.length.toLocaleString("es-CO")} registros del departamento`);
    } catch (error) {
      console.error(error);
      message.error("Error al filtrar por departamento");
    } finally { setLoadingTabla(false); }
  }, [pageSize, cargarPaginado]);

  // ── Handlers filtros de tabla ──────────────────────────────────────────────
  const handleDocumentoChange = (e) => {
    const valor = e.target.value.replace(/\D/g, "");
    setFiltroDocumento(valor);
    clearTimeout(debounceDocRef.current);
    debounceDocRef.current = setTimeout(async () => {
      const valorTrim = valor.trim();
      if (modoFiltro === "municipio" || modoFiltro === "departamento") return;
      if (!valorTrim) {
        if (modoFiltro === "documento") await cargarPaginado("", pageSize, true);
        return;
      }
      setLoadingTabla(true);
      try {
        const resultado = await obtenerInformeTitularesPorDocumento(valorTrim);
        if (!resultado?.success) return;
        setRawData(mapearFilas([resultado.data]));
        setModoFiltro("documento");
        setHasMore(false);
        message.success("Registro encontrado");
      } catch (error) {
        if (error?.response?.status === 404) {
          setRawData([]);
          setModoFiltro("documento");
          setHasMore(false);
          message.warning("No se encontró un titular con ese documento");
        } else {
          console.error(error);
          message.error("Error al buscar por documento");
        }
      } finally { setLoadingTabla(false); }
    }, DEBOUNCE_MS);
  };

  const handleEncuestaChange = useCallback(async (value) => {
    setFiltroEncuesta(value ?? null);
    if (modoFiltro === "municipio" || modoFiltro === "departamento") return;
    if (!value) {
      if (modoFiltro === "encuesta") await cargarPaginado("", pageSize, true);
      return;
    }
    setLoadingTabla(true);
    try {
      const resultado = await obtenerInformeTitularesPorEncuesta(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("encuesta");
      setHasMore(false);
      message.success(`${resultado.data.length.toLocaleString("es-CO")} registros encontrados`);
    } catch (error) {
      console.error(error);
      message.error("Error al filtrar por estado de encuesta");
    } finally { setLoadingTabla(false); }
  }, [modoFiltro, pageSize, cargarPaginado]);

  const handleParticipacionChange = useCallback(async (value) => {
    setFiltroParticipacion(value ?? null);
    if (modoFiltro === "municipio" || modoFiltro === "departamento") return;
    if (!value) {
      if (modoFiltro === "participacion") await cargarPaginado("", pageSize, true);
      return;
    }
    setLoadingTabla(true);
    try {
      const resultado = await obtenerInformeTitularesPorParticipacion(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("participacion");
      setHasMore(false);
      message.success(`${resultado.data.length.toLocaleString("es-CO")} registros encontrados`);
    } catch (error) {
      console.error(error);
      message.error("Error al filtrar por participación");
    } finally { setLoadingTabla(false); }
  }, [modoFiltro, pageSize, cargarPaginado]);

  const handleBeneficiarioChange = useCallback(async (value) => {
    setFiltroBeneficiario(value ?? null);
    if (modoFiltro === "municipio" || modoFiltro === "departamento") return;
    if (!value) {
      if (modoFiltro === "beneficiario") await cargarPaginado("", pageSize, true);
      return;
    }
    setLoadingTabla(true);
    try {
      const resultado = await obtenerInformeTitularesPorBeneficiario(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("beneficiario");
      setHasMore(false);
      message.success(`${resultado.data.length.toLocaleString("es-CO")} registros encontrados`);
    } catch (error) {
      console.error(error);
      message.error("Error al filtrar por beneficiario");
    } finally { setLoadingTabla(false); }
  }, [modoFiltro, pageSize, cargarPaginado]);

  const handleEncuestadorChange = useCallback(async (value) => {
    setFiltroEncuestador(value ?? null);
    if (!value) {
      if (modoFiltro === "encuestador") {
        await cargarPaginado("", pageSize, true);
      }
      return;
    }
    setLoadingTabla(true);
    try {
      const resultado =
        await obtenerInformeTitularesPorEncuestador(value);
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("encuestador");
      setHasMore(false);
      message.success(
        `${resultado.data.length.toLocaleString("es-CO")} registros encontrados`
      );
    } catch (error) {
      console.error(error);
      message.error("Error al filtrar por encuestador");
    } finally {
      setLoadingTabla(false);
    }
  }, [pageSize, cargarPaginado]);

  const handleAprobacionPagoChange = useCallback(async (value) => {
    setFiltroAprobacionPago(value ?? null);
    if (!value) {
      if (modoFiltro === "aprobacionPago") {
        await cargarPaginado("", pageSize, true);
      }
      return;
    }
    setLoadingTabla(true);
    try {
      const resultado =
        await obtenerInformeTitularesPorAprobacionPago(value);
        console.log(JSON.stringify(resultado, null, 2));
      if (!resultado?.success) return;
      setRawData(mapearFilas(resultado.data));
      setModoFiltro("aprobacionPago");
      setHasMore(false);
      message.success(
        `${resultado.data.length.toLocaleString("es-CO")} registros encontrados`
      );

    } catch (error) {
      console.error(error);
      message.error("Error al filtrar por aprobación de pago");
    } finally {
      setLoadingTabla(false);
    }
  }, [pageSize, cargarPaginado]);

  // ── dataFiltrada (filtro local sobre lo ya cargado) ────────────────────────
  const dataFiltrada = useMemo(() => {
    return rawData.filter((row) => {
      const coincideDocumento =
        !filtroDocumento ||
        row.numIdentificacion.toLowerCase().includes(filtroDocumento.toLowerCase());
      const coincideEncuesta =
        !filtroEncuesta || row.encuesta === filtroEncuesta.replace(/-/g, " ");
      const coincideParticipacion =
        !filtroParticipacion ||
        row.estadoParticipacion === filtroParticipacion.replace(/-/g, " ");
      const coincideBeneficiario =
        !filtroBeneficiario || row.beneficiario === filtroBeneficiario;
      const coincideEncuestador =
        !filtroEncuestador ||
        row._raw.encuestador_id === filtroEncuestador;

      const coincideAprobacionPago =
        !filtroAprobacionPago ||
        row.aprobacionPago === filtroAprobacionPago;
      return coincideDocumento && coincideEncuesta && coincideParticipacion && coincideBeneficiario && coincideEncuestador && coincideAprobacionPago ;
      
    });
  }, [rawData, filtroDocumento, filtroEncuesta, filtroParticipacion, filtroBeneficiario, filtroEncuestador, filtroAprobacionPago ]);

  const hayFiltrosActivos =
    !!filtroDocumento || !!filtroEncuesta || !!filtroParticipacion || !!filtroBeneficiario ||  !!filtroEncuestador || !!filtroAprobacionPago;

  // ── Limpiar filtros ────────────────────────────────────────────────────────
  const limpiarFiltrosSinRecargar = () => {
    setFiltroDocumento("");
    setFiltroEncuesta(null);
    setFiltroParticipacion(null);
    setFiltroBeneficiario(null);
    setMostrarResumenMunicipio(false);
    setFiltroEncuestador(null);
    setFiltroAprobacionPago(null);
    setRawDataResumen([]);
    clearTimeout(debounceDocRef.current);
  };

  const limpiarFiltros = () => {
    limpiarFiltrosSinRecargar();
    const modosFiltroSimple = ["paginado", "encuesta", "participacion", "beneficiario", "documento"];
    if (modosFiltroSimple.includes(modoFiltro)) {
      cargarPaginado("", pageSize, true);
    }
  };

  // ── Carga de datos completos para el resumen ───────────────────────────────
  const cargarDataResumen = useCallback(async () => {
    setLoadingResumen(true);
    setProgresoResumen(0);
    setFilasResumen(0);
    try {
      let dataCompleta = [];
      if (modoFiltro === "paginado") {
        const todos = await obtenerTodosInformeTitularesEnBloques(
          (pct, cargadas) => {
            setProgresoResumen(pct ?? 0);
            setFilasResumen(cargadas);
          },
          totalRegistros,
          500,
        );
        dataCompleta = mapearFilas(todos);
      } else {
        dataCompleta = rawData;
      }
      setRawDataResumen(dataCompleta);
    } catch (error) {
      console.error(error);
      message.error("Error al cargar el resumen por municipio");
    } finally {
      setLoadingResumen(false);
      setProgresoResumen(0);
      setFilasResumen(0);
    }
  }, [modoFiltro, totalRegistros, rawData]);

  // ── Toggle resumen por municipio ───────────────────────────────────────────
  const toggleResumenMunicipio = () => {
    const nuevo = !mostrarResumenMunicipio;
    setMostrarResumenMunicipio(nuevo);
    if (nuevo) {
      cargarDataResumen();
    } else {
      setRawDataResumen([]);
    }
  };

  // ── resumenMunicipioData calculado desde rawDataResumen ────────────────────
  const resumenMunicipioData = useMemo(() => {
    const base = rawDataResumen.length > 0 ? rawDataResumen : dataFiltrada;
    const mapa = {};
    base.forEach((row) => {
      const key = row.municipio;
      if (!mapa[key]) {
        mapa[key] = {
          key,
          departamento:    row.departamento,
          municipio:       row.municipio,
          totalTitulares:  0,
          realizadas:      0,
          noRealizadas:    0,
          participa:       0,
          noParticipa:     0,
          sinDefinir:      0,
          beneficiarioSi:  0,
          beneficiarioNo:  0,
        };
      }
      mapa[key].totalTitulares += 1;
      if (row.encuesta === "REALIZADA")                    mapa[key].realizadas    += 1;
      else                                                  mapa[key].noRealizadas  += 1;
      if (row.estadoParticipacion === "PARTICIPA")         mapa[key].participa     += 1;
      else if (row.estadoParticipacion === "NO PARTICIPA") mapa[key].noParticipa   += 1;
      else                                                  mapa[key].sinDefinir    += 1;
      if (row.beneficiario === "SI")                       mapa[key].beneficiarioSi += 1;
      else                                                  mapa[key].beneficiarioNo += 1;
    });
    return Object.values(mapa).sort((a, b) =>
      String(a.municipio).localeCompare(String(b.municipio))
    );
  }, [rawDataResumen, dataFiltrada]);

  // ── Excel ──────────────────────────────────────────────────────────────────
  const aplicarEncabezadoSheet = async (workbook, worksheet, titulo, colFin) => {
    try {
      const response = await fetch(logo);
      const buffer   = await response.arrayBuffer();
      const imageId  = workbook.addImage({ buffer, extension: "png" });
      worksheet.mergeCells("A1:B3");
      worksheet.addImage(imageId, { tl: { col: 0.2, row: 0.5 }, ext: { width: 180, height: 60 } });
    } catch { console.log("Logo no cargado"); }

    worksheet.mergeCells(`C1:${colFin}2`);
    const titleCell     = worksheet.getCell("C1");
    titleCell.value     = titulo;
    titleCell.font      = { size: 16, bold: true, color: { argb: "FF1B5E20" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    worksheet.mergeCells(`C3:${colFin}3`);
    const dateCell     = worksheet.getCell("C3");
    dateCell.value     = `Fecha de exportación: ${new Date().toLocaleDateString()}`;
    dateCell.alignment = { horizontal: "center" };
    worksheet.addRow([]);
  };

  const agregarTablaSheet = (worksheet, filas, headerColor = "FF217346") => {
    if (!filas.length) return;
    const headers   = Object.keys(filas[0]);
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font      = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: headerColor } };
      cell.alignment = { horizontal: "center" };
      cell.border    = {
        top: { style: "thin" }, bottom: { style: "thin" },
        left: { style: "thin" }, right: { style: "thin" },
      };
    });
    filas.forEach((fila) => {
      const row = worksheet.addRow(Object.values(fila));
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      });
    });
    worksheet.columns = headers.map((header) => ({
      width: Math.max(header.length, ...filas.map((row) => String(row[header] ?? "").length)) + 3,
    }));
  };

  const generarExcel = async (filasDetalle) => {
    if (!filasDetalle.length) { message.warning("No hay datos para exportar"); return; }

    const workbook = new ExcelJS.Workbook();

    const wsDetalle = workbook.addWorksheet("Informe General Titulares");
    await aplicarEncabezadoSheet(workbook, wsDetalle, "INFORME GENERAL DE TITULARES", "I");
    agregarTablaSheet(wsDetalle, filasDetalle, "FF1B5E20");

    const resumenExport = resumenMunicipioData.map((r) => ({
      "Departamento":            r.departamento,
      "Municipio":               r.municipio,
      "Total Titulares":         r.totalTitulares,
      "Encuestas Realizadas":    r.realizadas,
      "Encuestas No Realizadas": r.noRealizadas,
      "Participa":               r.participa,
      "No Participa":            r.noParticipa,
      "Sin Definir":             r.sinDefinir,
      "Beneficiario Sí":         r.beneficiarioSi,
      "Beneficiario No":         r.beneficiarioNo,
    }));

    if (resumenExport.length) {
      const wsResumen = workbook.addWorksheet("Resumen por Municipio");
      await aplicarEncabezadoSheet(workbook, wsResumen, "RESUMEN POR MUNICIPIO", "J");
      agregarTablaSheet(wsResumen, resumenExport, "FF1565C0");

      const totalRow = wsResumen.addRow([
        "TOTAL GENERAL", "",
        resumenMunicipioData.reduce((s, r) => s + r.totalTitulares, 0),
        resumenMunicipioData.reduce((s, r) => s + r.realizadas,     0),
        resumenMunicipioData.reduce((s, r) => s + r.noRealizadas,   0),
        resumenMunicipioData.reduce((s, r) => s + r.participa,      0),
        resumenMunicipioData.reduce((s, r) => s + r.noParticipa,    0),
        resumenMunicipioData.reduce((s, r) => s + r.sinDefinir,     0),
        resumenMunicipioData.reduce((s, r) => s + r.beneficiarioSi, 0),
        resumenMunicipioData.reduce((s, r) => s + r.beneficiarioNo, 0),
      ]);
      totalRow.eachCell((cell) => {
        cell.font   = { bold: true };
        cell.fill   = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F2FD" } };
        cell.border = {
          top: { style: "thin" }, bottom: { style: "double" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `InformeGeneralTitulares_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const descargarExcel = async () => {
    const exportarParcial = modoFiltro !== "paginado";

    if (exportarParcial) {
      if (!dataFiltrada.length) { message.warning("No hay datos para exportar"); return; }
      await generarExcel(mapearFilasExport(dataFiltrada.map((r) => r._raw)));
      message.success(`Excel exportado con ${dataFiltrada.length.toLocaleString("es-CO")} registros`);
      return;
    }

    setDescargando(true);
    setProgresoDescarga(0);
    setFilasDescargadas(0);
    try {
      const todos = await obtenerTodosInformeTitularesEnBloques(
        (pct, cargadas) => { setProgresoDescarga(pct ?? 0); setFilasDescargadas(cargadas); },
        totalRegistros,
        500,
      );
      setProgresoDescarga(100);
      await generarExcel(mapearFilasExport(todos));
      message.success(`Excel generado con ${todos.length.toLocaleString("es-CO")} registros`);
    } catch (error) {
      console.error(error);
      message.error("Error al generar el Excel");
    } finally {
      setDescargando(false);
      setProgresoDescarga(0);
      setFilasDescargadas(0);
    }
  };

  // ── Columnas tabla detalle ─────────────────────────────────────────────────
  const columns = [
    { title: "Departamento",      dataIndex: "departamento",      key: "departamento",      width: 150, fixed: "left" },
    { title: "Municipio",         dataIndex: "municipio",         key: "municipio",         width: 150, fixed: "left" },
    { title: "N° Identificación", dataIndex: "numIdentificacion", key: "numIdentificacion", width: 160, fixed: "left" },
    { title: "Nombre Completo",   dataIndex: "nombreCompleto",    key: "nombreCompleto",    width: 220, fixed: "left" },
    { title: "Celular",           dataIndex: "celular",           key: "celular",           width: 130 },
    { title: "Correo",            dataIndex: "correo",            key: "correo",            width: 220, ellipsis: true },
    {
      title: "Encuesta", dataIndex: "encuesta", key: "encuesta", width: 140, align: "center",
      render: etiquetaEncuesta,
    },
    {
      title: "Estado Participación", dataIndex: "estadoParticipacion", key: "estadoParticipacion",
      width: 170, align: "center",
      render: etiquetaParticipacion,
    },

    {
      title: "Encuestador", dataIndex: "encuestador", key: "encuestador",  width: 220, },
    {
      title: "Aprobación Pago", dataIndex: "aprobacionPago", key: "aprobacionPago", width: 150,
      align: "center",
      render: (valor) => (
        <Tag color={valor === "SI" ? "success" : "error"}>
          {valor}
        </Tag>
      ),
    },
    {
      title: "Entregas 1", dataIndex: "beneficiario", key: "beneficiario",
      width: 120, align: "center",
      render: etiquetaBeneficiario,
    },
    {
      title: "Entregas 2", dataIndex: "entrega2", key: "entrega2",
      width: 120, align: "center",
      render: etiquetaBeneficiario,
    },
    {
      title: "Entregas 3", dataIndex: "entrega3", key: "entrega3",
      width: 120, align: "center",
      render: etiquetaBeneficiario,
    },
  ];

  // ── Columnas resumen municipio ─────────────────────────────────────────────
  const columnsResumen = [
    { title: "Departamento",   dataIndex: "departamento",  key: "departamento" },
    { title: "Municipio",      dataIndex: "municipio",     key: "municipio" },
    {
      title: "Total", dataIndex: "totalTitulares", key: "totalTitulares", align: "center",
      render: (v) => <Tag color="blue">{v.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "Realizadas", dataIndex: "realizadas", key: "realizadas", align: "center",
      render: (v) => <Tag color="success">{v.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "No Realizadas", dataIndex: "noRealizadas", key: "noRealizadas", align: "center",
      render: (v) => <Tag color="default">{v.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "Participa", dataIndex: "participa", key: "participa", align: "center",
      render: (v) => <Tag color="success">{v.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "No Participa", dataIndex: "noParticipa", key: "noParticipa", align: "center",
      render: (v) => <Tag color="error">{v.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "Sin Definir", dataIndex: "sinDefinir", key: "sinDefinir", align: "center",
      render: (v) => <Tag color="warning">{v.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "Beneficiario Sí", dataIndex: "beneficiarioSi", key: "beneficiarioSi", align: "center",
      render: (v) => <Tag color="blue">{v.toLocaleString("es-CO")}</Tag>,
    },
    {
      title: "Beneficiario No", dataIndex: "beneficiarioNo", key: "beneficiarioNo", align: "center",
      render: (v) => <Tag color="default">{v.toLocaleString("es-CO")}</Tag>,
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Informe General de Titulares</Title>

      {/* ── Acciones ──────────────────────────────────────────────────────── */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Tooltip
          title={
            descargando
              ? `Descargando... ${filasDescargadas.toLocaleString("es-CO")} de ${totalRegistros.toLocaleString("es-CO")} registros`
              : modoFiltro === "paginado"
              ? `Exportar todos los registros (${totalRegistros.toLocaleString("es-CO")})`
              : `Exportar ${dataFiltrada.length.toLocaleString("es-CO")} registros`
          }
        >
          <Button
            icon={descargando ? <DownloadOutlined /> : <FileExcelOutlined />}
            onClick={descargarExcel}
            loading={descargando}
            disabled={descargando || dataFiltrada.length === 0}
            style={{ borderColor: "#217346", color: descargando ? undefined : "#217346" }}
          >
            {descargando ? `Descargando ${progresoDescarga}%` : "Descargar Excel"}
          </Button>
        </Tooltip>

        <Button
          icon={<BarChartOutlined />}
          type={mostrarResumenMunicipio ? "primary" : "default"}
          onClick={toggleResumenMunicipio}
          disabled={descargando || rawData.length === 0}
        >
          {mostrarResumenMunicipio ? "Ocultar resumen" : "Resumen por municipio"}
        </Button>
      </Space>

      {/* ── Barra de progreso de descarga Excel ───────────────────────────── */}
      {descargando && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress
            percent={progresoDescarga}
            status="active"
            strokeColor={{ "0%": "#217346", "100%": "#52c41a" }}
            format={() => (
              <Text style={{ fontSize: 12 }}>
                {filasDescargadas.toLocaleString("es-CO")} / {totalRegistros.toLocaleString("es-CO")}
              </Text>
            )}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Preparando archivo Excel, no cierres esta ventana...
          </Text>
        </div>
      )}

      {/* ── Barra de progreso del resumen — aparece aquí, ANTES de la tabla ── */}
      {mostrarResumenMunicipio && loadingResumen && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress
            percent={progresoResumen}
            status="active"
            strokeColor={{ "0%": "#1565C0", "100%": "#42A5F5" }}
            format={() => (
              <Text style={{ fontSize: 12 }}>
                {filasResumen.toLocaleString("es-CO")} / {totalRegistros.toLocaleString("es-CO")}
              </Text>
            )}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Cargando resumen completo, espera un momento...
          </Text>
        </div>
      )}

      {/* ── Selectores municipio / departamento ───────────────────────────── */}
      <Divider orientation="left" style={{ margin: "12px 0" }}>
        <Space><FilterOutlined /> Filtrar por municipio o departamento</Space>
      </Divider>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder="Filtrar por municipio..."
            style={{ width: "100%" }}
            value={municipioAdmin}
            onChange={handleMunicipioAdminChange}
            loading={loadingMunicipios}
            allowClear showSearch optionFilterProp="label"
            options={municipios.map((m) => ({ value: m.municipio, label: m.municipio }))}
            suffixIcon={<FilterOutlined />}
            disabled={descargando}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Select
            placeholder="Filtrar por departamento..."
            style={{ width: "100%" }}
            value={departamentoAdmin}
            onChange={handleDepartamentoAdminChange}
            loading={loadingDepartamentos}
            allowClear showSearch optionFilterProp="label"
            options={departamentos.map((d) => ({ value: d.departamento, label: d.departamento }))}
            suffixIcon={<FilterOutlined />}
            disabled={descargando}
          />
        </Col>
      </Row>

      {/* ── Tabla ─────────────────────────────────────────────────────────── */}
      <Spin spinning={loadingTabla} tip="Cargando datos..." size="large">
        <div style={{ minHeight: loadingTabla ? 400 : "auto" }}>

          {rawData.length > 0 && !loadingTabla && (
            <>
              <Divider orientation="left" style={{ margin: "12px 0" }}>
                Filtros de tabla
              </Divider>

              {/* ── Filtros secundarios ──────────────────────────────────── */}
              <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                <Col xs={24} sm={12} md={8} lg={5}>
                  <Input
                    placeholder="Buscar por N° documento"
                    prefix={<SearchOutlined style={{ color: "#bbb" }} />}
                    value={filtroDocumento}
                    onChange={handleDocumentoChange}
                    inputMode="numeric"
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Select
                    placeholder="Estado encuesta"
                    style={{ width: "100%" }}
                    value={filtroEncuesta}
                    onChange={handleEncuestaChange}
                    allowClear
                  >
                    <Select.Option value="REALIZADA">Realizada</Select.Option>
                    <Select.Option value="NO-REALIZADA">No realizada</Select.Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6} lg={4}>
                  <Select
                    placeholder="Participación"
                    style={{ width: "100%" }}
                    value={filtroParticipacion}
                    onChange={handleParticipacionChange}
                    allowClear
                  >
                    <Select.Option value="PARTICIPA">Participa</Select.Option>
                    <Select.Option value="NO-PARTICIPA">No participa</Select.Option>
                    <Select.Option value="SIN-DEFINIR">Sin definir</Select.Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={4} lg={3}>
                  <Select
                    placeholder="Beneficiario"
                    style={{ width: "100%" }}
                    value={filtroBeneficiario}
                    onChange={handleBeneficiarioChange}
                    allowClear
                  >
                    <Select.Option value="SI">Sí</Select.Option>
                    <Select.Option value="NO">No</Select.Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6} lg={5}>
                  <Select
                    placeholder="Encuestador"
                    style={{ width: "100%" }}
                    value={filtroEncuestador}
                    onChange={handleEncuestadorChange}
                    loading={loadingEncuestadores}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={encuestadores.map((e) => ({
                      value: e.encuestador_id,
                      label: `${e.encuestador_nombres} ${e.encuestador_apellidos}`,
                    }))}
                  />
                </Col>

                <Col xs={24} sm={12} md={4} lg={3}>
                  <Select
                    placeholder="Aprob. Pago"
                    style={{ width: "100%" }}
                    value={filtroAprobacionPago}
                    onChange={handleAprobacionPagoChange}
                    allowClear
                  >
                    <Select.Option value="SI">Sí</Select.Option>
                    <Select.Option value="NO">No</Select.Option>
                  </Select>
                </Col>
                {hayFiltrosActivos && (
                  <Col>
                    <Button icon={<ClearOutlined />} onClick={limpiarFiltros}>
                      Limpiar
                    </Button>
                  </Col>
                )}
              </Row>

              {/* ── Contador + paginación + tabla de detalle (ocultos en modo resumen) ── */}
              {!mostrarResumenMunicipio && (
                <>
                  <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                    <Col>
                      <Text type="secondary">
                        {modoFiltro === "paginado" ? (
                          <>
                            Mostrando{" "}
                            <Text strong>
                              {cursorIndex * pageSize + 1}–
                              {Math.min((cursorIndex + 1) * pageSize, totalRegistros)}
                            </Text>{" "}
                            de <Text strong>{totalRegistros.toLocaleString("es-CO")}</Text> registros
                            {" "}· página <Text strong>{cursorIndex + 1}</Text>
                          </>
                        ) : (
                          <>
                            Mostrando{" "}
                            <Text strong>{dataFiltrada.length.toLocaleString("es-CO")}</Text> registros
                          </>
                        )}
                        {hayFiltrosActivos && <Text type="warning"> (filtros aplicados)</Text>}
                      </Text>
                    </Col>
                    {modoFiltro === "paginado" && (
                      <Col>
                        <Space>
                          <Space size={4}>
                            <Text type="secondary" style={{ fontSize: 13 }}>Filas:</Text>
                            <Select
                              size="small"
                              value={pageSize}
                              onChange={handlePageSizeChange}
                              style={{ width: 70 }}
                              disabled={descargando}
                              options={PAGE_SIZE_OPTIONS.map((s) => ({ value: s, label: s }))}
                            />
                          </Space>
                          <Button
                            size="small" icon={<LeftOutlined />}
                            onClick={irPaginaAnterior}
                            disabled={cursorIndex === 0 || loadingTabla || descargando}
                          >
                            Anterior
                          </Button>
                          <Button
                            size="small" icon={<RightOutlined />} iconPosition="end"
                            onClick={irSiguientePagina}
                            disabled={!hasMore || loadingTabla || descargando}
                          >
                            Siguiente
                          </Button>
                        </Space>
                      </Col>
                    )}
                  </Row>

                  {/* ── Tabla detalle ──────────────────────────────────── */}
                  <Table
                    columns={columns}
                    dataSource={dataFiltrada}
                    rowKey="key"
                    size="middle"
                    bordered
                    scroll={{ x: 1720, y: 500 }}
                    pagination={
                      modoFiltro === "paginado"
                        ? false
                        : {
                            pageSize: 30,
                            showSizeChanger: true,
                            pageSizeOptions: ["30", "50", "100"],
                            showTotal: (total, range) =>
                              `${range[0]}–${range[1]} de ${total} registros`,
                          }
                    }
                  />
                </>
              )}
            </>
          )}

          {/* ── Resumen por municipio — FUERA del bloque de la tabla ──────── */}
          {mostrarResumenMunicipio && rawData.length > 0 && !loadingTabla && (
            <>
              <Divider orientation="left" style={{ marginTop: 32 }}>
                Resumen por municipio
              </Divider>

              {loadingResumen ? (
                // Placeholder vacío — la barra de progreso ya se muestra arriba
                <div style={{ padding: "24px 0" }}>
                  <Spin tip="Calculando resumen..." />
                </div>
              ) : resumenMunicipioData.length > 0 ? (
                <Table
                  columns={columnsResumen}
                  dataSource={resumenMunicipioData}
                  rowKey="key"
                  size="middle"
                  bordered
                  scroll={{ x: 900 }}
                  pagination={false}
                  summary={() => {
                    const totales = resumenMunicipioData.reduce(
                      (acc, r) => ({
                        totalTitulares: acc.totalTitulares + r.totalTitulares,
                        realizadas:     acc.realizadas     + r.realizadas,
                        noRealizadas:   acc.noRealizadas   + r.noRealizadas,
                        participa:      acc.participa      + r.participa,
                        noParticipa:    acc.noParticipa    + r.noParticipa,
                        sinDefinir:     acc.sinDefinir     + r.sinDefinir,
                        beneficiarioSi: acc.beneficiarioSi + r.beneficiarioSi,
                        beneficiarioNo: acc.beneficiarioNo + r.beneficiarioNo,
                      }),
                      {
                        totalTitulares: 0, realizadas: 0, noRealizadas: 0,
                        participa: 0,      noParticipa: 0, sinDefinir: 0,
                        beneficiarioSi: 0, beneficiarioNo: 0,
                      }
                    );
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}>
                          <Text strong>Total general</Text>
                        </Table.Summary.Cell>
                        {[
                          ["blue",    totales.totalTitulares],
                          ["success", totales.realizadas],
                          ["default", totales.noRealizadas],
                          ["success", totales.participa],
                          ["error",   totales.noParticipa],
                          ["warning", totales.sinDefinir],
                          ["blue",    totales.beneficiarioSi],
                          ["default", totales.beneficiarioNo],
                        ].map(([color, val], i) => (
                          <Table.Summary.Cell key={i} index={i + 2} align="center">
                            <Tag color={color}>{val.toLocaleString("es-CO")}</Tag>
                          </Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                    );
                  }}
                />
              ) : (
                <Text type="secondary">No hay datos para mostrar en el resumen.</Text>
              )}
            </>
          )}

          {/* ── Empty state ───────────────────────────────────────────────── */}
          {rawData.length === 0 && !loadingTabla && (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <img
                src="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                alt="sin datos"
                style={{ width: 120, marginBottom: 16, opacity: 0.45 }}
              />
              <Text style={{ fontSize: 16, color: "#8c8c8c" }}>
                No se encontraron registros
              </Text>
            </div>
          )}
        </div>
      </Spin>
    </Card>
  );
};

export default InformeGeneralTitulares;
